import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { companyName } from "../../../constants/constants";
import logo from "../../../assets/logo.svg";

export type HeaderNavItem = {
  href: string;
  label: string;
  ariaLabel?: string;
};

export type HeaderProps = React.ComponentPropsWithoutRef<"header"> & {
  brand?: React.ReactNode;
  navItems?: HeaderNavItem[];
  rightSlot?: React.ReactNode;
  sticky?: boolean;
  bordered?: boolean;
  containerClassName?: string;
  navClassName?: string;
  mobileMenuEnabled?: boolean;
  mobileMenuBadgeCount?: number;
};

const Header: React.FC<HeaderProps> = ({
  brand = (
    <span className="inline-flex items-center gap-2">
      <img src={logo} alt={`${companyName} logo`} className="h-8 w-8 object-contain" />
      <span>{companyName}</span>
    </span>
  ),
  navItems = [],
  rightSlot,
  sticky = false,
  bordered = true,
  className,
  containerClassName,
  navClassName,
  mobileMenuEnabled = true,
  mobileMenuBadgeCount = 0,
  ...props
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const isInternalRoute = (href: string): boolean => href.startsWith("/") && !href.startsWith("//");
  const hasMobileMenu = mobileMenuEnabled && (navItems.length > 0 || rightSlot);
  const normalizedMobileMenuBadgeCount = Math.max(0, mobileMenuBadgeCount);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!mobileMenuRef.current) {
        return;
      }

      const target = event.target;
      if (target instanceof Node && !mobileMenuRef.current.contains(target)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  return (
    <header
      className={clsx(
        "w-full bg-myteengig-surface text-myteengig-text-primary",
        bordered && "border-b border-myteengig-border",
        sticky && "sticky top-0 z-50",
        className,
      )}
      {...props}
    >
      <div
        className={clsx(
          "mx-auto grid w-full max-w-6xl grid-cols-[1fr_auto] items-center gap-4 px-2 py-3 sm:px-6 md:grid-cols-[1fr_auto_1fr]",
          containerClassName,
        )}
      >
        <div className="min-w-0 justify-self-start text-lg font-bold tracking-tight">{brand}</div>

        <nav
          aria-label="Primary navigation"
          className={clsx("hidden items-center gap-4 text-sm font-medium md:flex", navClassName)}
        >
          {navItems.map((item) => (
            isInternalRoute(item.href) ? (
              <Link
                key={`${item.href}-${item.label}`}
                to={item.href}
                aria-label={item.ariaLabel}
                className="transition-colors hover:text-myteengig-primary"
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={`${item.href}-${item.label}`}
                href={item.href}
                aria-label={item.ariaLabel}
                className="transition-colors hover:text-myteengig-primary"
              >
                {item.label}
              </a>
            )
          ))}
        </nav>

        <div className={clsx(mobileMenuEnabled ? "hidden justify-self-end md:block" : "justify-self-end")}>
          {rightSlot ? <div className="flex items-center gap-2">{rightSlot}</div> : null}
        </div>

        {hasMobileMenu ? (
          <div ref={mobileMenuRef} className="relative ml-auto justify-self-end md:hidden">
            {isMobileMenuOpen ? (
              <button
                type="button"
                aria-label="Close mobile menu overlay"
                className="fixed inset-0 z-40 cursor-default bg-transparent"
                onClick={() => setIsMobileMenuOpen(false)}
              />
            ) : null}
            <button
              type="button"
              aria-expanded={isMobileMenuOpen}
              aria-label={normalizedMobileMenuBadgeCount > 0 ? `Toggle mobile menu, ${normalizedMobileMenuBadgeCount} notifications` : "Toggle mobile menu"}
              className="relative z-50 inline-flex h-9 w-9 items-center justify-center rounded-full border border-myteengig-border bg-white text-myteengig-text-primary shadow-xs transition hover:border-myteengig-primary hover:text-myteengig-primary"
              onClick={() => setIsMobileMenuOpen((current) => !current)}
            >
              <span className="sr-only">Menu</span>
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-[18px] w-[18px]"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {isMobileMenuOpen ? (
                  <>
                    <path d="M6 6l12 12" />
                    <path d="M18 6L6 18" />
                  </>
                ) : (
                  <>
                    <path d="M4 7h16" />
                    <path d="M4 12h16" />
                    <path d="M4 17h16" />
                  </>
                )}
              </svg>
              {normalizedMobileMenuBadgeCount > 0 ? (
                <span
                  aria-hidden="true"
                  className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-myteengig-primary px-1 text-[11px] font-semibold leading-none text-white"
                >
                  {normalizedMobileMenuBadgeCount > 99 ? "99+" : normalizedMobileMenuBadgeCount}
                </span>
              ) : null}
            </button>
            {isMobileMenuOpen ? (
              <div
                className="fixed left-1/2 top-[4.25rem] z-50 w-[min(18rem,calc(100vw-1.5rem))] -translate-x-1/2 overflow-hidden rounded-2xl border border-myteengig-border bg-white p-3 shadow-xl"
                onClick={() => setIsMobileMenuOpen(false)}
              >
              {navItems.length > 0 ? (
                <nav aria-label="Mobile navigation" className="flex flex-col gap-1">
                  {navItems.map((item) => (
                    isInternalRoute(item.href) ? (
                      <Link
                        key={`mobile-${item.href}-${item.label}`}
                        to={item.href}
                        aria-label={item.ariaLabel}
                        className="rounded-xl px-3 py-3 text-sm font-medium text-myteengig-text-primary transition-colors hover:bg-myteengig-background hover:text-myteengig-primary"
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <a
                        key={`mobile-${item.href}-${item.label}`}
                        href={item.href}
                        aria-label={item.ariaLabel}
                        className="rounded-xl px-3 py-3 text-sm font-medium text-myteengig-text-primary transition-colors hover:bg-myteengig-background hover:text-myteengig-primary"
                      >
                        {item.label}
                      </a>
                    )
                  ))}
                </nav>
              ) : null}

              {rightSlot ? (
                <div className={clsx("mt-2 flex flex-col gap-2 border-t border-myteengig-border pt-3", navItems.length === 0 && "mt-0 border-t-0 pt-0")}>
                  {rightSlot}
                </div>
              ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
};

export default Header;
